import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Multilingual from '~~utils/Multilingual';
import { LeagueResource, SeasonResource, GlossaryResource } from '~~apis/resource';
import Loading from '~~elements/Loading';
import Tournament from './Tournament';
import { TournamentProvider } from './useTournament';
import PageRejection from '../common/PageRejection';
import { useRootState } from '../../useRoot';
import { find } from 'ramda';

const NonOrganizerDiv = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const getPermitted = (permissionList, organizerUniqid) => {
  if (!permissionList) {
    return false;
  }

  if (permissionList.platform === 'ADMIN') {
    return true;
  }

  return !!find((l) => l.info.uniqid === organizerUniqid, permissionList.leagues);
};

const Index = () => {
  const {
    loginInfo: { isLogin },
    permissionList,
  } = useRootState();
  const { organizer_uniqid: organizerUniqid, tournament_uniqid: tournamentUniqid } = useParams();

  const [isPlatformAdmin, setIsPlatformAdmin] = useState(false);
  const [permitted, setPermitted] = useState(false);
  const [process, setProcess] = useState('LOADING');
  const [organizer, setOrganizer] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [games, setGames] = useState([]);
  const [groups, setGroups] = useState([]);
  const [teams, setTeams] = useState([]);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    setPermitted(getPermitted(permissionList, organizerUniqid));
    setIsPlatformAdmin(permissionList?.platform === 'ADMIN');
  }, [organizerUniqid, permissionList]);

  useEffect(() => {
    if (!permitted) {
      return;
    }

    LeagueResource.getLeagueWithSeasons({ leagueUniqid: organizerUniqid })
      .then(({ data }) => {
        setOrganizer(data);
        const targetTournament = find((s) => s.uniqid === tournamentUniqid, data.seasons);
        if (!targetTournament) {
          setProcess('NOT_FOUND');
          return;
        }

        let apiCountDown = 5;
        const apiDone = () => {
          apiCountDown--;
          if (apiCountDown > 0) {
            return;
          }

          setProcess('DONE');
        };

        SeasonResource.getSeason({ seasonUniqid: tournamentUniqid }).then(({ data }) => {
          setTournament(data);
          apiDone();
        });
        SeasonResource.getGames({ seasonUniqid: tournamentUniqid }).then(({ data }) => {
          setGames(data);
          apiDone();
        });
        SeasonResource.getGroups({ seasonUniqid: tournamentUniqid }).then(({ data }) => {
          setGroups(data);
          apiDone();
        });
        SeasonResource.getTeams({ seasonUniqid: tournamentUniqid }).then(({ data }) => {
          setTeams(data);
          apiDone();
        });
        GlossaryResource.getLocations().then(({ data }) => {
          setLocations(data);
          apiDone();
        });
      })
      .catch((e) => {
        setProcess('ERROR');
      });
  }, [organizerUniqid, permitted, tournamentUniqid]);

  if (!isLogin) {
    return (
      <NonOrganizerDiv>
        <PageRejection text={Multilingual('COMMON.PAGE_FORBIDDEN_FOR_GUEST')} />
      </NonOrganizerDiv>
    );
  }

  if (!permissionList) {
    return (
      <NonOrganizerDiv>
        <Loading width="30vw" />
      </NonOrganizerDiv>
    );
  }

  if (!permitted) {
    return (
      <NonOrganizerDiv>
        <PageRejection text={Multilingual('COMMON.PAGE_FORBIDDEN_FOR_CURRENT_USER')} />
      </NonOrganizerDiv>
    );
  }

  switch (process) {
    case 'LOADING':
      return (
        <NonOrganizerDiv>
          <Loading width="30vw" />
        </NonOrganizerDiv>
      );
    case 'NOT_FOUND':
      return (
        <NonOrganizerDiv>
          <PageRejection text={Multilingual('COMMON.RESOURCE_NOT_FOUND')} />
        </NonOrganizerDiv>
      );
    case 'ERROR':
      return (
        <NonOrganizerDiv>
          <PageRejection text={Multilingual('COMMON.UNKNOWN_ERROR')} />
        </NonOrganizerDiv>
      );
    default:
      return (
        <TournamentProvider>
          <Tournament
            isPlatformAdmin={isPlatformAdmin}
            organizer={organizer}
            tournament={tournament}
            games={games}
            groups={groups}
            teams={teams}
            locations={locations}
          />
        </TournamentProvider>
      );
  }
};

export default Index;
